import {
  validateMaxAnswers,
  validateMinAnswers,
  validateMaxSlider,
  validateMinSlider,
  validateSliderAccuracy,
  validateMaxSumOfValuesEqualsTo,
  validateMinSumOfEqualsTo,
  validateSumEqual,
  validateDisplayColumns,
  validateMaximumChars,
  validateMinimumChars,
  validateMinNumber,
  validateMaxNumber,
  validateMaximumWordCount,
  validateMinimumWordCount,
} from '@/utils/advancedOptions.helpers'

import { questionsConfig } from '@/utils/questions'

import { LANGUAGES, QUESTION_TYPES } from '@/utils/constants'

const { PORTUGUESE, ENGLISH, SPANISH } = LANGUAGES

export const DEFAULT_ADVANCED_OPTIONS = {
  slider_max: null,
  slider_layout: null,
  slider_min: null,
  slider_middlestart: null,
  slider_showminmax: null,
  slider_accuracy: null,
  infinite_cards: null,

  matrix_display: null,

  num_value_int_only: null,
  max_num_value_n: null,
  min_num_value_n: null,
  max_sum: null,
  min_sum: null,
  sum_equal: null,
  prefix: null,
  suffix: null,
  cascade_validation: null,
  max_value_reference: null,
  min_value_reference: null,

  maximum_chars: null,

  minimum_chars: null,
  maximum_word_count: null,
  minimum_word_count: null,

  multiple_evaluation: null,

  size: 'small',
  enforce_format: null,

  facialcoding: null,
  enforce_facialcoding: null,

  display_columns: null,

  max_answers: null,
  min_answers: null,

  array_filter: null,
  array_filter_reverse: null,
  advanced_inheritance: null,

  display_as_select: null,
  display_as_slider: null,

  button_lock_seconds: null,

  random_order: false,
  multi_alternative_random: false,
  exclusive: [],
  others: null,
  is_param: null,
  none_of_the_above: null,
  numeric_prefix: null,
  numeric_suffix: null,
}

const unmapped = {
  treatedIndividually: true,
  type: 'un-mapped',
  required: false,
}

const getPhoneLabels = (country, languageSpecifics = {}) => ({
  [PORTUGUESE]: `${languageSpecifics[PORTUGUESE] || country}: Telefone`,
  [ENGLISH]: `${languageSpecifics[ENGLISH] || country}: Phone Number`,
  [SPANISH]: `${languageSpecifics[SPANISH] || country}: Teléfono`,
})

export const advancedOptionToRenderConfig = {
  array_filter: {
    type: 'select',

    displayText: {
      [PORTUGUESE]: 'Herdar Respostas Selecionadas de:',
      [ENGLISH]: 'Inherit Answers Selected From:',
      [SPANISH]: 'Heredar Respuestas Seleccionadas de:',
    },

    treatedIndividually: false,
    required: false,
    emptySelectOption: true,

    disableChecker: ({ array_filter_reverse, advanced_inheritance }) =>
      !!array_filter_reverse || !!advanced_inheritance,

    optionsGetter: (questions, questionIndex, questionGroupsBefore) => {
      const priorQuestions = questionGroupsBefore
        .map(({ questions }) => questions)
        .flat()
        .filter(({ type }) => questionsConfig[type].canBeReferencedInArrayFilter)
        .map(({ code }) => ({
          label: code,
          value: code,
        }))

      const actualGroupQuestons = questions
        .filter(
          ({ type }, index) =>
            index < questionIndex && questionsConfig[type].canBeReferencedInArrayFilter,
        )
        .map(({ code }) => ({
          label: code,
          value: code,
        }))

      return [...priorQuestions, ...actualGroupQuestons]
    },

    copyOption: ({ value, codeTranslator }) => (value ? codeTranslator(value) : value),
  },

  array_filter_reverse: {
    type: 'select',

    displayText: {
      [PORTUGUESE]: 'Herdar Respostas NÃO Selecionadas de:',
      [ENGLISH]: 'Inherit Answers NOT Selected From:',
      [SPANISH]: 'Heredar Respuestas NO Seleccionadas de:',
    },
    treatedIndividually: false,
    required: false,
    emptySelectOption: true,

    disableChecker: ({ array_filter, advanced_inheritance }) =>
      !!array_filter || !!advanced_inheritance,

    optionsGetter: (questions, questionIndex, questionGroupsBefore) => {
      const priorQuestions = questionGroupsBefore
        .map(({ questions }) => questions)
        .flat()
        .filter(({ type }) => questionsConfig[type].canBeReferencedInArrayFilterReverse)
        .map(({ code }) => ({
          label: code,
          value: code,
        }))

      const actualGroupQuestons = questions
        .filter(
          ({ type }, index) =>
            index < questionIndex &&
            questionsConfig[type].canBeReferencedInArrayFilterReverse,
        )
        .map(({ code }) => ({
          label: code,
          value: code,
        }))

      return [...priorQuestions, ...actualGroupQuestons]
    },

    copyOption: ({ value, codeTranslator }) => (value ? codeTranslator(value) : value),
  },

  advanced_inheritance: {
    type: 'custom',
    displayText: {
      [PORTUGUESE]: 'Herdar Respostas de maneira avançada',
      [ENGLISH]: 'Advanced inheritance:',
      [SPANISH]: 'Advanced inheritance:',
    },
    treatedIndividually: true,
    required: false,

    disableChecker: ({ array_filter, array_filter_reverse }) =>
      !!array_filter || !!array_filter_reverse,

    copyOption: ({ value, codeTranslator, idControl }) => {
      if (!value) return value

      const { qid, code } = value.origin

      const newId = idControl[qid]

      if (!newId) return value

      return {
        ...value,
        origin: {
          ...value.origin,
          qid: newId,
          code: codeTranslator(code),
        },
      }
    },
  },

  button_lock_seconds: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Tempo para bloquear botão de prosseguir (segundos)',
      [ENGLISH]: 'Block proceed button for (seconds)',
      [SPANISH]: 'Bloquear el botón de proceder durante (segundos)',
    },
    treatedIndividually: false,
    minValue: 1,
    required: false,
    validator: ({ currentValue = 0 }) => {
      return validateDisplayColumns(currentValue)
    },
  },

  display_columns: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Número de Colunas a Serem Exibidas:',
      [ENGLISH]: 'Number of Columns to Display:',
      [SPANISH]: 'Número de Columnas para Mostrar:',
    },
    treatedIndividually: false,
    minValue: 1,
    required: false,
    validator: ({ currentValue = 0 }) => {
      return validateDisplayColumns(currentValue)
    },
  },

  display_as_select: {
    type: 'boolean',
    displayText: {
      [PORTUGUESE]: 'Exibir Opções como Lista Suspensa:',
      [ENGLISH]: 'Display Options as Dropdown List:',
      [SPANISH]: 'Visualización en lista desplegable:',
    },
    disableChecker: ({ display_as_slider }) => !!display_as_slider,
    treatedIndividually: false,
    required: false,
  },

  display_as_slider: {
    type: 'boolean',
    disableChecker: ({ display_as_select }) => !!display_as_select,
    displayText: {
      [PORTUGUESE]: 'Exibir Opções como Slider:',
      [ENGLISH]: 'Display Options as Slider:',
      [SPANISH]: 'Visualización en slider:',
    },
    treatedIndividually: false,
    required: false,
  },

  enforce_facialcoding: {
    ...unmapped,
  },

  exclusive: {
    ...unmapped,
  },

  facialcoding: {
    ...unmapped,
  },

  fix_random: {
    ...unmapped,
  },

  is_param: {
    type: 'text-input',
    displayText: {
      [PORTUGUESE]: 'Parâmetro de Consulta:',
      [ENGLISH]: 'Query Parameter:',
      [SPANISH]: 'Parámetro de Consulta:',
    },
    treatedIndividually: false,
    required: false,
    validator: value => ({ canUpdate: true, valueToUpdate: value }), // to update
  },

  matrix_display: {
    type: 'binary-with-toggle',
    displayText: {
      [PORTUGUESE]: 'Aplicar Layout:',
      [ENGLISH]: 'Enforce Layout:',
      [SPANISH]: 'Aplicar Visualización:',
    },
    required: false,
    treatedIndividually: false,
    toggleValues: [
      {
        value: 'carousel',
        display: {
          [PORTUGUESE]: 'Carrossel',
          [ENGLISH]: 'Carousel',
          [SPANISH]: 'Carrusel',
        },
      },
      {
        value: 'table',
        display: {
          [PORTUGUESE]: 'Tabela',
          [ENGLISH]: 'Table',
          [SPANISH]: 'Tabla',
        },
      },
    ],
  },

  max_answers: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Número Máximo de Respostas:',
      [ENGLISH]: 'Maximum Number of Answers:',
      [SPANISH]: 'Número Máximo de Respuestas:',
    },
    treatedIndividually: false,
    minValue: null,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMaxAnswers(currentValue, questionIndex, groupQuestions)
    },
  },

  max_sum: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Valor Máximo da Soma:',
      [ENGLISH]: 'Maximum Sum Value:',
      [SPANISH]: 'Valor Máximo de la Suma:',
    },
    treatedIndividually: false,
    minValue: 1,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMaxSumOfValuesEqualsTo(currentValue, questionIndex, groupQuestions)
    },
    disableChecker: ({ sum_equal }) => Boolean(sum_equal),
  },

  maximum_chars: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Máximo de Caracteres:',
      [ENGLISH]: 'Maximum Chars:',
      [SPANISH]: 'Caracteres Máximos:',
    },
    treatedIndividually: false,
    minValue: 1,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMaximumChars(currentValue, questionIndex, groupQuestions)
    },
    disableChecker: ({ maximum_word_count, minimum_word_count }) =>
      Boolean(maximum_word_count || minimum_word_count),
  },

  minimum_chars: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Caracteres Mínimos:',
      [ENGLISH]: 'Minimum Chars:',
      [SPANISH]: 'Caracteres Mínimos:',
    },
    treatedIndividually: false,
    minValue: 0,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMinimumChars(currentValue, questionIndex, groupQuestions)
    },
    disableChecker: ({ maximum_word_count, minimum_word_count }) =>
      Boolean(maximum_word_count || minimum_word_count),
  },

  maximum_word_count: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Número Máximo de Palavras:',
      [ENGLISH]: 'Maximum Word Count:',
      [SPANISH]: 'Número Máximo de Palabras:',
    },
    treatedIndividually: false,
    minValue: 1,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMaximumWordCount(currentValue, questionIndex, groupQuestions)
    },
    disableChecker: ({ maximum_chars, minimum_chars }) =>
      Boolean(maximum_chars || minimum_chars),
  },

  minimum_word_count: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Número Mínimo de Palavras:',
      [ENGLISH]: 'Minimum Word Count:',
      [SPANISH]: 'Número Mínimo de Palabras:',
    },
    treatedIndividually: false,
    minValue: 0,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMinimumWordCount(currentValue, questionIndex, groupQuestions)
    },
    disableChecker: ({ maximum_chars, minimum_chars }) =>
      Boolean(maximum_chars || minimum_chars),
  },

  min_answers: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Número Mínimo de Respostas:',
      [ENGLISH]: 'Minimum Number of Answers:',
      [SPANISH]: 'Número Mínimo de Respuestas:',
    },
    treatedIndividually: false,
    minValue: 1,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMinAnswers(currentValue, questionIndex, groupQuestions)
    },
  },

  max_num_value_n: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Valor Máximo:',
      [ENGLISH]: 'Ending/Max. Value:',
      [SPANISH]: 'Valor Máximo:',
    },
    treatedIndividually: false,

    disableChecker: ({ max_value_reference }) => !!max_value_reference,

    minValue: null,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMaxNumber(currentValue, questionIndex, groupQuestions)
    },
  },

  max_value_reference: {
    type: 'select',

    displayText: {
      [PORTUGUESE]: 'Limite Máximo Com Base Em:',
      [ENGLISH]: 'Max Value Based On:',
      [SPANISH]: 'Valor Máximo Igual A',
    },

    treatedIndividually: false,
    required: false,
    emptySelectOption: true,

    disableChecker: ({ max_num_value_n }) => typeof max_num_value_n === 'number',

    optionsGetter: (questions, questionIndex, questionGroupsBefore, currentOptions) => {
      const priorQuestions = questionGroupsBefore
        .map(({ questions }) => questions)
        .flat()
        .filter(
          ({ type, qid }) =>
            type === QUESTION_TYPES.number && currentOptions.min_value_reference !== qid,
        )
        .map(({ code, qid }) => ({
          label: code,
          value: qid,
        }))

      const actualGroupQuestons = questions
        .filter(
          ({ type, qid }, index) =>
            index < questionIndex &&
            type === QUESTION_TYPES.number &&
            currentOptions.min_value_reference !== qid,
        )
        .map(({ code, qid }) => ({
          label: code,
          value: qid,
        }))

      return [...priorQuestions, ...actualGroupQuestons]
    },

    copyOption: ({ value, idControl }) => (value ? idControl[value] : value),
  },

  min_num_value_n: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Valor Mínimo:',
      [ENGLISH]: 'Starting/Min. Value:',
      [SPANISH]: 'Valor Mínimo:',
    },

    disableChecker: ({ min_value_reference }) => !!min_value_reference,

    treatedIndividually: false,
    minValue: 0,
    required: false,

    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMinNumber(currentValue, questionIndex, groupQuestions)
    },
  },

  min_value_reference: {
    type: 'select',

    displayText: {
      [PORTUGUESE]: 'Limite Mínimo Com Base Em:',
      [ENGLISH]: 'Min Value Based On:',
      [SPANISH]: 'Valor Mínimo Igual A',
    },

    treatedIndividually: false,
    required: false,
    emptySelectOption: true,

    disableChecker: ({ min_num_value_n }) => typeof min_num_value_n === 'number',

    optionsGetter: (questions, questionIndex, questionGroupsBefore, currentOptions) => {
      const priorQuestions = questionGroupsBefore
        .map(({ questions }) => questions)
        .flat()
        .filter(
          ({ type, qid }) =>
            type === QUESTION_TYPES.number && currentOptions.max_value_reference !== qid,
        )
        .map(({ code, qid }) => ({
          label: code,
          value: qid,
        }))

      const actualGroupQuestons = questions
        .filter(
          ({ type, qid }, index) =>
            index < questionIndex &&
            type === QUESTION_TYPES.number &&
            currentOptions.max_value_reference !== qid,
        )
        .map(({ code, qid }) => ({
          label: code,
          value: qid,
        }))

      return [...priorQuestions, ...actualGroupQuestons]
    },

    copyOption: ({ value, idControl }) => (value ? idControl[value] : value),
  },

  min_sum: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Valor Mínimo da Soma:',
      [ENGLISH]: 'Min. Sum of Values:',
      [SPANISH]: 'Valor de suma (mín):',
    },
    treatedIndividually: false,
    minValue: 0,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMinSumOfEqualsTo(currentValue, questionIndex, groupQuestions)
    },
    disableChecker: ({ sum_equal }) => Boolean(sum_equal),
  },

  num_value_int_only: {
    type: 'boolean',
    displayText: {
      [PORTUGUESE]: 'Somente Respostas Inteiras:',
      [ENGLISH]: 'Integer Answers Only:',
      [SPANISH]: 'Solo Números Enteros:',
    },
    treatedIndividually: false,
    required: false,
  },

  multiple_evaluation: {
    type: 'boolean',
    displayText: {
      [PORTUGUESE]: 'Evaluação múltipla:',
      [ENGLISH]: 'Multiple evaluation:',
      [SPANISH]: 'Evaluação múltipla:',
    },
    treatedIndividually: false,
    required: false,
  },

  none_of_the_above: {
    type: 'custom',
    displayText: {
      [PORTUGUESE]: 'Adicionar opção "não quero responder"',
      [ENGLISH]: 'Add "None of the above" Option',
      [SPANISH]: 'Agregar la opción "No quiero responder"',
    },

    treatedIndividually: true,

    required: false,

    copyOption: ({ value, idControl }) => {
      if (!value) return value

      const { logic } = value

      const newId = idControl[logic]

      if (!newId) return value

      return {
        ...value,
        logic: newId,
      }
    },
  },

  others: {
    type: 'text-input',
    displayText: {
      [PORTUGUESE]: '"Outra" opção:',
      [ENGLISH]: '"Other" option:',
      [SPANISH]: 'Opción "Otro":',
    },
    treatedIndividually: false,
    required: false,
    validator: value => ({ canUpdate: true, valueToUpdate: value }), // to update
  },

  prefix: {
    type: 'text-input',

    displayText: {
      [PORTUGUESE]: 'Prefixo:',
      [ENGLISH]: 'Prefix:',
      [SPANISH]: 'Prefixo:',
    },

    treatedIndividually: false,

    required: false,

    maxLength: 10,

    validator: value => ({ canUpdate: value, valueToUpdate: value }), // to update

    disableChecker: ({ size }) => size && size !== 'small',
    resetValudOnDisable: true,
  },

  suffix: {
    type: 'text-input',

    displayText: {
      [PORTUGUESE]: 'Sufixo:',
      [ENGLISH]: 'Suffix:',
      [SPANISH]: 'Sufixo:',
    },

    treatedIndividually: false,

    required: false,

    validator: value => ({ canUpdate: true, valueToUpdate: value }), // to update

    maxLength: 10,

    disableChecker: ({ size }) => size && size !== 'small',

    resetValudOnDisable: true,
  },

  random_order: {
    ...unmapped,
  },

  multi_alternative_random: {
    ...unmapped,
  },

  size: {
    type: 'select',
    displayText: {
      [PORTUGUESE]: 'Tamanho',
      [ENGLISH]: 'Size:',
      [SPANISH]: 'Tamaño',
    },
    required: true,
    treatedIndividually: false,
    emptySelectOption: false,
    options: [
      {
        value: 'small',
        label: {
          [PORTUGUESE]: 'Pequeno',
          [ENGLISH]: 'Small',
          [SPANISH]: 'Pequeño',
        },
      },
      {
        value: 'medium',
        label: {
          [PORTUGUESE]: 'Médio',
          [ENGLISH]: 'Medium',
          [SPANISH]: 'Medio',
        },
      },
      {
        value: 'large',
        label: {
          [PORTUGUESE]: 'Grande',
          [ENGLISH]: 'Large',
          [SPANISH]: 'Grande',
        },
      },
    ],
  },

  cascade_validation: {
    type: 'select',
    displayText: {
      [PORTUGUESE]: 'Validação em Cascata:',
      [ENGLISH]: 'Cascade Validation:',
      [SPANISH]: 'Validação en Cascata:',
    },
    required: false,
    treatedIndividually: false,
    emptySelectOption: true,
    options: [
      {
        value: 'incremental',
        label: {
          [PORTUGUESE]: 'O próximo valor deve ser sempre maior',
          [ENGLISH]: 'Next value should always be bigger',
          [SPANISH]: 'El siguiente valor siempre debe ser mayor',
        },
      },
      {
        value: 'incremental_or_equal',
        label: {
          [PORTUGUESE]: 'O próximo valor deve ser sempre maior ou igual',
          [ENGLISH]: 'Next value should always be bigger or equal',
          [SPANISH]: 'El siguiente valor siempre debe ser mayor o igual',
        },
      },
      {
        value: 'decremental',
        label: {
          [PORTUGUESE]: 'O próximo valor deve ser sempre menor',
          [ENGLISH]: 'Next value should always be smaller',
          [SPANISH]: 'El siguiente valor siempre debe ser más pequeño',
        },
      },
      {
        value: 'decremental_of_equal',
        label: {
          [PORTUGUESE]: 'O próximo valor deve ser sempre menor ou igual',
          [ENGLISH]: 'Next value should always be smaller or equal',
          [SPANISH]: 'El siguiente valor siempre debe ser menor o igual',
        },
      },
    ],
  },

  slider_accuracy: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Precisão do Slider:',
      [ENGLISH]: 'Slider Accuracy:',
      [SPANISH]: 'Precisión del Slider:',
    },
    treatedIndividually: false,
    minValue: 1,
    required: true,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateSliderAccuracy(currentValue, questionIndex, groupQuestions)
    },
  },

  slider_layout: {
    type: 'boolean',
    displayText: {
      [PORTUGUESE]: 'Layout do Slider:',
      [ENGLISH]: 'Slider Layout:',
      [SPANISH]: 'Diseño del Slider:',
    },
    treatedIndividually: false,
    required: false,
  },

  slider_max: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Valor Máximo:',
      [ENGLISH]: 'Ending/Max. Value:',
      [SPANISH]: 'Valor Máximo:',
    },
    treatedIndividually: false,
    minValue: null,
    required: true,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMaxSlider(currentValue, questionIndex, groupQuestions)
    },
  },

  slider_middlestart: {
    type: 'boolean',
    displayText: {
      [PORTUGUESE]: 'Botão no Meio:',
      [ENGLISH]: 'Middle Start:',
      [SPANISH]: 'Botón en el Medio:',
    },
    treatedIndividually: false,
    required: false,
  },

  slider_min: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Valor Mínimo:',
      [ENGLISH]: 'Starting/Min. Value:',
      [SPANISH]: 'Valor Mínimo:',
    },
    treatedIndividually: false,
    minValue: null,
    required: true,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateMinSlider(currentValue, questionIndex, groupQuestions)
    },
  },

  slider_showminmax: {
    type: 'boolean',
    displayText: {
      [PORTUGUESE]: 'Mostrar Valores:',
      [ENGLISH]: 'Show Values:',
      [SPANISH]: 'Mostrar Valores:',
    },
    treatedIndividually: false,
    required: false,
  },

  sum_equal: {
    type: 'numeric-input',
    displayText: {
      [PORTUGUESE]: 'Soma dos Valores é Igual a:',
      [ENGLISH]: 'Sum of Values Equals to:',
      [SPANISH]: 'Valor Exacto de la Suma:',
    },
    treatedIndividually: false,
    minValue: 1,
    required: false,
    validator: ({ currentValue = 0, questionIndex, groupQuestions }) => {
      return validateSumEqual(currentValue, questionIndex, groupQuestions)
    },
    disableChecker: ({ max_sum, min_sum }) => Boolean(max_sum || min_sum),
  },

  infinite_cards: {
    type: 'boolean',
    displayText: {
      [PORTUGUESE]: 'Cards Infinitos:',
      [ENGLISH]: 'Infinite Cards:',
      [SPANISH]: 'Tarjetas Infinitas:',
    },
    treatedIndividually: false,
    required: false,
  },

  numeric_prefix: {
    type: 'text-input',
    displayText: {
      [PORTUGUESE]: 'Sufixo:',
      [ENGLISH]: 'Suffix:',
      [SPANISH]: 'Sufijo:',
    },
    treatedIndividually: false,
    required: false,
    maxLength: 5,
  },

  numeric_suffix: {
    type: 'text-input',
    displayText: {
      [PORTUGUESE]: 'Prefixo:',
      [ENGLISH]: 'Prefix:',
      [SPANISH]: 'Prefijo:',
    },
    treatedIndividually: false,
    required: false,
    maxLength: 5,
  },

  enforce_format: {
    type: 'select',
    displayText: {
      [PORTUGUESE]: 'Validação de Texto:',
      [ENGLISH]: 'Enforce Format:',
      [SPANISH]: 'Validación de Texto:',
    },
    required: false,
    treatedIndividually: false,
    emptySelectOption: true,
    options: [
      { value: 'email', label: 'E-mail' },
      {
        value: 'argentina_phone',
        label: getPhoneLabels('ARGENTINA'),
      },
      {
        value: 'brazil_phone',
        label: getPhoneLabels('BRAZIL', { [PORTUGUESE]: 'BRASIL' }),
      },
      {
        value: 'bolivia_phone',
        label: getPhoneLabels('BOLIVIA'),
      },
      {
        value: 'colombia_phone',
        label: getPhoneLabels('COLOMBIA'),
      },
      {
        value: 'chile_phone',
        label: getPhoneLabels('CHILE'),
      },
      {
        value: 'costa_rica_phone',
        label: getPhoneLabels('COSTA RICA'),
      },
      {
        value: 'equador_phone',
        label: getPhoneLabels('ECUADOR', { [PORTUGUESE]: 'EQUADOR' }),
      },
      {
        value: 'el_salavador_phone',
        label: getPhoneLabels('EL SALVADOR'),
      },
      {
        value: 'guatemala_phone',
        label: getPhoneLabels('GUATEMALA'),
      },
      {
        value: 'honduras_phone',
        label: getPhoneLabels('HONDURAS'),
      },
      {
        value: 'mexico_phone',
        label: getPhoneLabels('MEXICO'),
      },
      {
        value: 'nicaragua_phone',
        label: getPhoneLabels('NICARAGUA'),
      },
      {
        value: 'panama_phone',
        label: getPhoneLabels('PANAMÁ'),
      },
      {
        value: 'paraguay_phone',
        label: getPhoneLabels('PARAGUAY', { [PORTUGUESE]: 'PARAGUAI' }),
      },
      {
        value: 'peru_phone',
        label: getPhoneLabels('PERU'),
      },
      {
        value: 'uruguay_phone',
        label: getPhoneLabels('URUGUAY', { [PORTUGUESE]: 'URUGUAI' }),
      },
      {
        value: 'us_phone',
        label: getPhoneLabels('US'),
      },
      {
        value: 'venezuela_phone',
        label: getPhoneLabels('VENEZUELA'),
      },
    ],
  },
}

export const copyAdvancedOptions = (options, idControl, codeTranslator) => {
  const updated = Object.fromEntries(
    Object.entries(options).map(([option, value]) => [
      option,
      advancedOptionToRenderConfig[option].copyOption?.({
        value,
        idControl,
        codeTranslator,
      }) || value,
    ]),
  )

  return updated
}
